import { BehaviorSubject } from 'rxjs';
import ResponseHandlerService from './ResponseHandlerService';
import { USER_ROLE_TYPE } from '../constants/general';
import ClientApi from '../api/client/index';
import CoreApi from '../api/core';
import { actionByRole } from 'lib/helpers';
import centerEndpoints from '../api/resources/center';
import { LOCAL_USER_TYPE } from 'constants/localStorage';

export interface ICenter {
  id: number;
  name: string;
  groups: {}[];
  babies: {}[];
  external_id: string;
  access_token?: {};
  idClientApi?: number;
}

export const defaultCenter = {
  id: undefined,
};
 
class CenterService {
  static instance: CenterService;
  private centerList$: BehaviorSubject<ICenter[]>;
  public activeCenter$ = new BehaviorSubject(defaultCenter);
  public loadingCenters$ = new BehaviorSubject(false);
  private center$: BehaviorSubject<any>;
  private GroupList$: BehaviorSubject<any>;
  private GroupsByCenter$: BehaviorSubject<any>;
  private MyGroupList$: BehaviorSubject<any>;
  private groupDetail$: BehaviorSubject<any>;
  private usersCenter$: BehaviorSubject<any>;
  public centerToken$ = new BehaviorSubject('');
  public detailedCenters$ = new BehaviorSubject([]);
  private respHandler = ResponseHandlerService.getInstance();

  constructor() {
    this.centerList$ = new BehaviorSubject<ICenter[]>([]);
    this.center$ = new BehaviorSubject<any>({});
    this.GroupList$ = new BehaviorSubject<any>([]);
    this.GroupsByCenter$ = new BehaviorSubject<any>({});
    this.MyGroupList$ = new BehaviorSubject<any>([]);
    this.groupDetail$ = new BehaviorSubject<any>({});
    this.usersCenter$ = new BehaviorSubject<any>([]);
  }

  static getInstance(): CenterService {
    if (!CenterService.instance) CenterService.instance = new CenterService();
    return CenterService.instance;
  }

  /* Setters */

  setCenterToken = (token: any) => this.centerToken$.next(token);

  setActiveCenter$ = (center: any) => this.activeCenter$.next(center);

  setDetailedCenters = (centers: any) => this.detailedCenters$.next(centers);

  setCenterList$ = (centerList: any[]) => this.centerList$.next(centerList);

  /* Getters */

  getCenterToken = () => this.centerToken$;

  getActiveCenter$ = () => this.activeCenter$;

  getCenterList$ = () => this.centerList$;

  getGroupDetail = () => this.groupDetail$;

  getMyCenter = () => this.center$;

  getGroupList = () => this.GroupList$;

  getGroupsByCenter = () => this.GroupsByCenter$;

  getMyGroupList = () => this.MyGroupList$;

  getUsersCenter = () => this.usersCenter$;

  getDetailedCenters = () => this.detailedCenters$;

  getLoadingCenters$ = () => this.loadingCenters$;

  /* CREATES */

  addCenterConfig = (data: object, token: string) =>
    centerEndpoints.postCenterConfig(data, token);

  addCenter = (data: object) =>
    ClientApi.organizationScope[USER_ROLE_TYPE]
    .postCenter(data)
    .then((response: any) => {
      this.fetchDetailedCenters();
      this.fetchCenters();
      return response;
    });

  /* READS */

  fetchCenter = async (centerId?: number) => {
    try {
      const data = await actionByRole(LOCAL_USER_TYPE(), {
        owner: () => centerEndpoints.getCenterAsOwner(centerId),
        admin: () => centerEndpoints.getCenterAsAdmin(centerId),
        director: () => centerEndpoints.getCenterAsDirector(centerId),
        teacher: () => centerEndpoints.getCenterAsTeacher(centerId as number),
      });

      this.activeCenter$.next(data);

      return data;
    } catch (error) {
      return error;
    }
  };

  fetchMyCenter = () =>
    CoreApi.centerScope.getCenter().then((response: any) => {
      this.center$.next(response);

      return response;
    });

  fetchUsersCenter = () =>
    ClientApi.organizationScope[
      USER_ROLE_TYPE
    ].getUserCenter().then((res: any) => this.usersCenter$.next(res));

  fetchGroupList = () =>
    ClientApi.organizationScope[
      USER_ROLE_TYPE
    ].getGroupList().then((res: any) => this.GroupList$.next(res));

  fetchCenters = async () => {
    try {
      this.loadingCenters$.next(true);
      const centers = await actionByRole(LOCAL_USER_TYPE(), {
        owner: centerEndpoints.getCentersAsOwner,
        admin: centerEndpoints.getCentersAsAdmin,
        director: centerEndpoints.getCentersAsDirector,
        teacher: centerEndpoints.getCentersAsTeacher,
      });
      this.loadingCenters$.next(false);
      this.centerList$.next(centers);
      return centers;
    } catch (error) {
      return error;
    }
  };

  fetchGroupDetail = (groupId: number) =>
    CoreApi.centerScope
      .getGroupDetail(groupId)
      .then((res: any) => this.groupDetail$.next(res?.group));

  fetchDetailedCenters = () => {
    return this.respHandler.handleResponse({
      promise: CoreApi.organizationScope.getCenters(),
      successCallback: (res: any) => this.detailedCenters$.next(res),
    });
  };

  /* UPDATES */
  // NOTE: This method is Legacy
  updateCenter = (centerId: number, data: object) =>
    ClientApi.organizationScope[USER_ROLE_TYPE].putCenter(centerId, data).then(
      (res: any) => {
        this.fetchCenters();
        return res.center;
      },
    );

  putCenter = (centerId: number, body: Record<string, any>) =>  this.respHandler.handleResponse({
    promise: actionByRole(LOCAL_USER_TYPE(), {
      owner: () => centerEndpoints.putCenterAsOwner(centerId, body),
      admin: () => centerEndpoints.putCenterAsOwner(centerId, body),
      director: () => centerEndpoints.putCenterAsOwner(centerId, body),
      teacher: () => new Promise((resolve, reject) => reject(new Error('This action cannot be done by teachers'))),
    }),
  });

  editTeacherPermission = (body: any, centerId: number, teacherId: number) =>
    ClientApi.organizationScope[USER_ROLE_TYPE].updateTeacherPermission(
      body,
      centerId,
      teacherId,
    );

  /* DELETES */

  deleteCenter = (centerId: number) =>
    ClientApi.organizationScope[USER_ROLE_TYPE].deleteCenter(centerId).then(
      () => {
        this.fetchCenters();
      },
    );

    putCenterConfig = (body: any, centerId: number,) =>  this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => centerEndpoints.putCenterConfig(centerId, body),
        admin: () => centerEndpoints.putCenterConfig(centerId, body),
        director: () => centerEndpoints.putCenterConfig(centerId, body),
        teacher: () => new Promise((resolve, reject) => reject(new Error('This action cannot be done by teachers'))),
      }),
    });

    getCenterConfig = (centerId: number,) =>  this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => centerEndpoints.getCenterConfig(centerId),
        admin: () => centerEndpoints.getCenterConfig(centerId),
        director: () => centerEndpoints.getCenterConfig(centerId),
        teacher: () => new Promise((resolve, reject) => reject(new Error('This action cannot be done by teachers'))),
      }),
    });

  fetchCenterActiveConfig = (centerId: number = 0) =>
    actionByRole(LOCAL_USER_TYPE(), { 
      owner: () => centerEndpoints.getCenterActiveConfig(centerId),
      admin: () => centerEndpoints.getCenterActiveConfig(centerId),
      director: () => centerEndpoints.getCenterActiveConfig(centerId),
      teacher: () => centerEndpoints.getCenterActiveConfigForTeacher(),
    })

  /*
  * This method will allow to request all centers' data
  */
  requestCentersData = (params: any) => {
    return centerEndpoints.postExportData(params);
  };
}

export default CenterService;
